import { COPY_TO_CAMPAIGN } from "@lib/graphql/mutations";
import { CAMPAIGNS_QUERY } from "@lib/graphql/queries";
import { Copy, X } from "lucide-react";
import { useState } from "react";
import { useMutation, useQuery } from "urql";

interface CopyToCampaignDialogProps {
  sourceCampaignId: string;
  candidateId: string;
  candidateName: string;
  onDone: () => void;
  onCancel: () => void;
}

interface CampaignOption {
  id: string;
  title: string;
  status: string;
}

export function CopyToCampaignDialog({
  sourceCampaignId,
  candidateId,
  candidateName,
  onDone,
  onCancel,
}: CopyToCampaignDialogProps) {
  const [targetCampaignId, setTargetCampaignId] = useState("");
  const [preserveNotes, setPreserveNotes] = useState(false);

  // Campaign picker: one large page (field max_page_size 250), not paginated.
  const [{ data }] = useQuery({ query: CAMPAIGNS_QUERY, variables: { first: 250 } });
  const [{ fetching }, copyToCampaign] = useMutation(COPY_TO_CAMPAIGN);

  const campaigns: CampaignOption[] = (data?.campaigns?.edges ?? [])
    .map((edge: { node: CampaignOption }) => edge.node)
    .filter((c: CampaignOption) => c.id !== sourceCampaignId);

  const handleCopy = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetCampaignId) return;
    const result = await copyToCampaign({
      input: {
        sourceCampaignId,
        targetCampaignId,
        candidateId,
        preserveNotes,
      },
    });
    if (result.data?.copyToCampaign?.errors?.length === 0) {
      onDone();
    }
  };

  return (
    <form onSubmit={handleCopy} className="rounded border border-border bg-card-alt p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-xs font-medium text-text-secondary">
          <Copy size={12} /> Copy {candidateName} to another campaign
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="rounded p-0.5 text-text-faint hover:bg-card-hover"
        >
          <X size={12} />
        </button>
      </div>

      <div>
        <select
          value={targetCampaignId}
          onChange={(e) => setTargetCampaignId(e.target.value)}
          className="w-full rounded border border-border bg-surface px-3 py-2 text-sm"
          required
        >
          <option value="">Select campaign...</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title} ({c.status})
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 text-xs text-text-muted">
        <input
          type="checkbox"
          checked={preserveNotes}
          onChange={(e) => setPreserveNotes(e.target.checked)}
          className="rounded"
        />
        Copy notes from source campaign
      </label>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={fetching || !targetCampaignId}
          className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        >
          {fetching ? "Copying..." : "Copy"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded border border-border px-3 py-1.5 text-xs text-text-muted hover:bg-card-hover"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
